import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Loader, EmptyState, StarRating } from "@/components/common";
import { PageTitle, Panel } from "./AdminShell";
import { Star, Trash2, CheckCircle2, EyeOff } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { delivery: "Livraison", installation: "Installation", product: "Produit" };

export default function AdminReviews() {
  const [reviews, setReviews] = useState(null);
  const [tab, setTab] = useState("pending");
  const [selected, setSelected] = useState(new Set());
  const load = useCallback(() => api.get("/reviews").then((r) => { setReviews(r.data); setSelected(new Set()); }), []);
  useEffect(() => { load(); }, [load]);
  const publish = async (id, val) => { await api.put(`/reviews/${id}/publish`, null, { params: { published: val } }); toast.success(val ? "Avis publié" : "Avis masqué"); load(); };
  const del = async (id) => { if (!window.confirm("Supprimer définitivement cet avis ?")) return; await api.delete(`/reviews/${id}`); toast.success("Avis supprimé"); load(); };

  const bulk = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    if (action === "delete" && !window.confirm(`Supprimer définitivement ${ids.length} avis ?`)) return;
    try { const { data } = await api.post("/reviews/bulk", { ids, action }); toast.success(`${data.count} avis traité(s)`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  if (!reviews) return <Loader />;

  const list = tab === "pending" ? reviews.filter((r) => !r.published) : tab === "published" ? reviews.filter((r) => r.published) : reviews;
  const pendingCount = reviews.filter((r) => !r.published).length;

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = list.length > 0 && list.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected((s) => { const n = new Set(s); if (allSelected) list.forEach((r) => n.delete(r.id)); else list.forEach((r) => n.add(r.id)); return n; });

  return (
    <div>
      <PageTitle>Avis clients</PageTitle>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("pending")} data-testid="review-tab-pending" className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "pending" ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>À valider ({pendingCount})</button>
        <button onClick={() => setTab("published")} data-testid="review-tab-published" className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "published" ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>Publiés</button>
        <button onClick={() => setTab("all")} data-testid="review-tab-all" className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "all" ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>Tous</button>
      </div>

      {list.length === 0 ? (
        <EmptyState icon={Star} title="Aucun avis" subtitle="Les avis clients apparaîtront ici." />
      ) : (
        <>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="review-select-all" /> Tout sélectionner
            </label>
            {selected.size > 0 && (
              <div className="flex items-center gap-2" data-testid="bulk-toolbar">
                <span className="text-sm font-semibold text-slate-700">{selected.size} sélectionné(s)</span>
                <button onClick={() => bulk("publish")} className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#C85A32] text-white inline-flex items-center gap-1" data-testid="bulk-publish"><CheckCircle2 className="w-3.5 h-3.5" /> Publier</button>
                <button onClick={() => bulk("unpublish")} className="text-xs font-bold px-3 py-1.5 rounded-lg bg-slate-200 text-slate-700 inline-flex items-center gap-1" data-testid="bulk-unpublish"><EyeOff className="w-3.5 h-3.5" /> Masquer</button>
                <button onClick={() => bulk("delete")} className="text-xs font-bold px-3 py-1.5 rounded-lg bg-red-50 text-red-600 inline-flex items-center gap-1" data-testid="bulk-delete"><Trash2 className="w-3.5 h-3.5" /> Supprimer</button>
              </div>
            )}
          </div>
          <div className="space-y-3">
            {list.map((r) => (
              <Panel key={r.id} className={`p-4 ${selected.has(r.id) ? "ring-1 ring-[#C85A32]" : ""}`} data-testid={`review-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`review-check-${r.id}`} />
                  <StarRating value={r.rating} readOnly size={16} />
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{TYPE_LABEL[r.target_type] || r.target_type}</span>
                  {r.verified && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Achat vérifié</span>}
                  <span className="text-xs text-slate-500">{r.customer_name}{r.target_name ? ` · ${r.target_name}` : ""} · {formatDate(r.created_at)}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <button onClick={() => publish(r.id, !r.published)} data-testid={`publish-${r.id}`}
                      className={`text-xs font-bold px-3 py-1.5 rounded-lg ${r.published ? "bg-green-100 text-green-700" : "bg-[#C85A32] text-white"}`}>
                      {r.published ? "Publié ✓ (masquer)" : "Publier"}
                    </button>
                    <button onClick={() => del(r.id)} data-testid={`delete-review-${r.id}`} title="Supprimer" className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-2">{r.comment}</p>}
                {r.sub_ratings && Object.keys(r.sub_ratings).length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                    {Object.entries(r.sub_ratings).map(([k, v]) => <span key={k}>{k}: {v}★</span>)}
                  </div>
                )}
              </Panel>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
